'use client';
import { useState } from 'react';
import { isMobile } from 'react-device-detect';
import { SvgAdd } from '@icons/designSystemIcons/SvgAdd';
import { SvgCross } from '@icons/designSystemIcons/SvgCross';
import { Product } from '@interface/product';
import { Button } from 'designSystem/Buttons/Buttons';
import Carousel from 'designSystem/Carousel/Carousel';
import { Dialog, DialogContent } from 'designSystem/Dialog/Dialog';
import { Flex } from 'designSystem/Layouts/Layouts';
import { Title } from 'designSystem/Texts/Texts';
import Image from 'next/image';

export default function ProductImagesCarousel({
  product,
  visibleSlides,
  zoomOverImages = false,
}: {
  product: Product;
  visibleSlides?: number;
  zoomOverImages?: boolean;
}) {
  const [zoomedImage, setZoomedImage] = useState<number | null>(null);

  const handleCloseZoomedImage = () => {
    return zoomedImage === null;
  };

  return (
    <Flex className="w-full md:gap-16 items-start">
      {zoomOverImages && (
        <Dialog
          open={zoomedImage !== null}
          onOpenChange={handleCloseZoomedImage}
        >
          <DialogContent
            hideClose
            className="w-full bg-derma-secondary300 right-0"
          >
            <Button
              size="xl"
              type="secondary"
              className="absolute bottom-4 right-4"
              customStyles="h-auto w-auto p-1"
            >
              <SvgCross
                className="cursor-pointer h-6 w-6 appearance-none p-1"
                onClick={() => setZoomedImage(null)}
              />
            </Button>

            <div className="w-full h-full border flex items-center justify-center">
              <Carousel
                hasControls
                controlstyles="px-4 mt-0"
                currentSlide={zoomedImage ? zoomedImage : 0}
              >
                {product.beforeAndAfterImages
                  ?.sort(function x(a, b) {
                    return a.urlBefore!.localeCompare(b.urlBefore!, undefined, {
                      numeric: true,
                      sensitivity: 'base',
                    });
                  })
                  .map(item => (
                    <Image
                      key={item.urlBefore}
                      src={item.urlBefore || ''}
                      unoptimized
                      alt={'antes y despues ' + product.title}
                      height={1200}
                      width={1200}
                      className="rounded-3xl p-4"
                    />
                  ))}
              </Carousel>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Carousel
        controlstyles="px-4"
        hasControls={product.beforeAndAfterImages?.length > 1}
        dragEnabled={product.beforeAndAfterImages?.length > 1}
        touchEnabled={product.beforeAndAfterImages?.length > 1}
        visibleSlides={
          visibleSlides
            ? visibleSlides
            : product.beforeAndAfterImages?.length > 1
              ? isMobile
                ? 1
                : 3
              : 1
        }
        hasCounter={isMobile && product.beforeAndAfterImages?.length > 1}
        className={`md:px-0 rounded-xl ${
          product.beforeAndAfterImages?.length < 2 && !isMobile ? 'w-1/2' : ''
        }
        `}
      >
        {product.beforeAndAfterImages
          ?.sort(function x(a, b) {
            return a.urlBefore!.localeCompare(b.urlBefore!, undefined, {
              numeric: true,
              sensitivity: 'base',
            });
          })
          .map((item, index) => (
            <div className="px-4 productImageSlide" key={item.id}>
              <div className="relative w-full">
                {zoomOverImages && (
                  <div className="absolute top-3 right-3 z-10">
                    <Button
                      size="sm"
                      type="secondary"
                      customStyles="p-1 h-auto w-auto"
                      onClick={() =>
                        zoomOverImages ? setZoomedImage(index) : null
                      }
                    >
                      <SvgAdd className="" />
                    </Button>
                  </div>
                )}
                <div itemScope itemType="https://schema.org/ImageObject">
                  <div className="relative w-full">
                    <Image
                      src={item.urlBefore || ''}
                      unoptimized
                      alt={'antes y despues ' + product.title}
                      width={1200}
                      height={800}
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      className="w-full h-auto rounded-3xl"
                    />
                    <span className="hidden" itemProp="license">
                      https://www.holaglow.com/aviso-legal
                    </span>
                    <span className="hidden" itemProp="contentUrl">
                      {item.urlBefore}
                    </span>
                    <span
                      className="hidden"
                      itemProp="creator"
                      itemType="https://schema.org/Organization"
                      itemScope
                    >
                      <meta itemProp="name" content="Holaglow" />
                    </span>
                    <span className="hidden" itemProp="creditText">
                      Holaglow
                    </span>
                    <span className="hidden" itemProp="copyrightNotice">
                      Glow Lab SL
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
      </Carousel>
      {product.beforeAndAfterImages?.length <= 1 && !isMobile && (
        <div className="w-1/2">
          <Title size="2xl" className="text-left font-bold mb-4">
            Consigue el resultado que deseas
          </Title>
        </div>
      )}
    </Flex>
  );
}
